export function base64UrlToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
  const binary = atob(padded);
  return new Uint8Array(Array.from(binary, char => char.charCodeAt(0)));
}

export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  const base64 = btoa(binary);
  let end = base64.length;
  while (end > 0 && base64[end - 1] === '=') {
    end--;
  }
  return base64.substring(0, end).replace(/\+/g, '-').replace(/\//g, '_');
}

export interface RegistrationOptions {
  readonly rp: { readonly name: string; readonly id?: string };
  readonly user: { readonly id: string; readonly name: string; readonly displayName: string };
  readonly challenge: string;
  readonly pubKeyCredParams: readonly { readonly type: string; readonly alg: number }[];
  readonly timeout?: number;
  readonly excludeCredentials?: readonly { readonly id: string; readonly type: string }[];
  readonly authenticatorSelection?: {
    readonly authenticatorAttachment?: string;
    readonly requireResidentKey?: boolean;
    readonly residentKey?: string;
    readonly userVerification?: string;
  };
  readonly attestation?: string;
}

export interface CredentialResponse {
  readonly credentialId: string;
  readonly rawCredentialId: string;
  readonly credentialType: string;
  readonly authenticatorAttachment: string;
  readonly clientExtensionResults: AuthenticationExtensionsClientOutputs;
  readonly response: {
    readonly clientDataJSON: string;
    readonly attestationObject: string;
  };
}

export function buildCredentialCreationOptions(
  registrationOptions: RegistrationOptions,
): PublicKeyCredentialCreationOptions {
  const excludeCredentials = registrationOptions.excludeCredentials
    ? registrationOptions.excludeCredentials.map(cred => ({
      id: base64UrlToUint8Array(cred.id),
      type: cred.type as PublicKeyCredentialType,
    }))
    : undefined;

  return {
    rp: {
      name: registrationOptions.rp.name,
      id: registrationOptions.rp.id || 'ebanx.com',
    },
    user: {
      name: registrationOptions.user.name,
      displayName: registrationOptions.user.displayName,
      id: base64UrlToUint8Array(registrationOptions.user.id),
    },
    challenge: base64UrlToUint8Array(registrationOptions.challenge),
    pubKeyCredParams: registrationOptions.pubKeyCredParams as PublicKeyCredentialParameters[],
    timeout: registrationOptions.timeout,
    excludeCredentials,
    authenticatorSelection: registrationOptions.authenticatorSelection as AuthenticatorSelectionCriteria | undefined,
    attestation: registrationOptions.attestation as AttestationConveyancePreference | undefined,
  };
}

export function extractCredentialResponse(credential: PublicKeyCredential): CredentialResponse {
  const attestationResponse = credential.response as AuthenticatorAttestationResponse;

  return {
    credentialId: credential.id,
    rawCredentialId: arrayBufferToBase64Url(credential.rawId),
    credentialType: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment || 'platform',
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: arrayBufferToBase64Url(attestationResponse.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(attestationResponse.attestationObject),
    },
  };
}

export function getWebAuthnErrorMessage(error: Error): string {
  switch (error.name) {
    case 'NotAllowedError':
      return 'Autentica\u00E7\u00E3o cancelada ou negada.';
    case 'NotSupportedError':
      return 'Seu navegador n\u00E3o suporta autentica\u00E7\u00E3o biom\u00E9trica.';
    case 'SecurityError':
      return 'Erro de seguran\u00E7a. Verifique o dom\u00EDnio.';
    default:
      if (error.message && error.message.includes('timeout')) {
        return 'Tempo limite de autentica\u00E7\u00E3o excedido.';
      }
      return error.message || 'Falha na autentica\u00E7\u00E3o.';
  }
}
